import { Component, inject, OnInit, output, signal, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import {
  applyWhen,
  FieldContext,
  form,
  maxLength,
  minLength,
  required,
  validate,
  ValidationResult,
} from "@angular/forms/signals";
import {
  AudienceExpressionType,
  AudienceMatchType,
  AudienceTargeting,
  CampaignType,
  type SdStrategyCreateRequest,
  Strategy,
  StrategyStateEnum,
  StrategyTactic,
  TacticType,
} from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { SdTargetingType } from "@front/m19-models";
import { AccountSelectionService, SdStrategyService } from "@front/m19-services";
import { IButtonDirective, IStepperComponent, MODAL_DATA, ModalRef, ToasterService } from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { validateAlgoMode } from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";
import { FormNavigationService, IFormStep } from "@m19-board/shared/form-navigation/form-navigation.service";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";
import {
  SdCreateStrategyAsinsComponent,
  SdCreateStrategyAsinsModel,
} from "@m19-board/sponsored-display/sd-manager/sd-strategy-creation/steps/sd-create-strategy-asins.component";
import {
  SdCreateStrategySetupComponent,
  SdCreateStrategySetupModel,
} from "@m19-board/sponsored-display/sd-manager/sd-strategy-creation/steps/sd-create-strategy-setup.component";
import { SdCreateStrategyTargetingsComponent } from "@m19-board/sponsored-display/sd-manager/sd-strategy-creation/steps/sd-create-strategy-targetings.component";
import { SpStrategyCreationModalData } from "@m19-board/sponsored-product/sp-page/sp-substrategy-creation/sp-strategy-creation.component";
import { Router } from "@angular/router";
import { finalize, map, switchMap, take } from "rxjs";
import { SdTargetingFormModel } from "@m19-board/shared/sd-targeting-form/sd-targeting-form.types";

interface SdStrategyCreationFormModel {
  setup: SdCreateStrategySetupModel;
  targetings: SdTargetingFormModel;
  asins: SdCreateStrategyAsinsModel;
}

@Component({
  selector: "app-sd-strategy-creation",
  templateUrl: "./sd-strategy-creation.component.html",
  providers: [FormNavigationService],
  imports: [
    IButtonDirective,
    ReactiveFormsModule,
    TranslocoDirective,
    IStepperComponent,
    FormShellComponent,
    SdCreateStrategySetupComponent,
    SdCreateStrategyTargetingsComponent,
    SdCreateStrategyAsinsComponent,
  ],
  host: {
    class: "block h-full min-h-0",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdStrategyCreationComponent implements OnInit {
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly sdStrategyService = inject(SdStrategyService);
  private readonly strategyService = inject(StrategyService);
  private readonly router = inject(Router);
  private readonly formNavigationService = inject(FormNavigationService<SdStrategyCreationFormModel>);
  protected readonly modalData = inject<SpStrategyCreationModalData>(MODAL_DATA, { optional: true });
  private readonly modalRef = inject(ModalRef, { optional: true });

  readonly strategyCreated = output<Strategy>();

  readonly accountMarketplace = toSignal(this.accountSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  readonly strategies = toSignal(
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
      map((strategies) => Array.from(strategies.values())),
    ),
  );

  formModel = signal<SdStrategyCreationFormModel>({
    setup: {
      strategyName: Utils.generateRandomName(),
      algoModeConfig: null,
    },
    targetings: {
      targetingType: null,
      segment: null,
      audienceExpressionType: AudienceExpressionType.views,
      audienceMatchType: AudienceMatchType.exactProduct,
      audienceLookback: 30,
      isValid: false,
    },
    asins: {
      asins: this.modalData?.asins ?? [],
    },
  });

  creationForm = form(
    this.formModel,
    (schema) => {
      required(schema.setup.strategyName);
      maxLength(schema.setup.strategyName, 80);
      required(schema.setup.algoModeConfig);
      validateAlgoMode(schema.setup.algoModeConfig);

      required(schema.targetings);
      validate(schema.targetings, ({ value }) => (value()?.isValid ? null : { kind: "invalidTargetings" }));

      required(schema.asins.asins);
      minLength(schema.asins.asins, 1);
    },
    {
      submission: {
        action: async () => this.submitForm(),
      },
    },
  );

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;

  isCreatingStrategy = signal(false);

  ngOnInit(): void {
    this.formNavigationService.configure({
      form: this.creationForm,
      blocked: this.isCreatingStrategy,
      steps: [
        {
          stepId: "setup",
          label: this.translocoService.translate("common.setup"),
          size: "sm",
        },
        {
          stepId: "targetings",
          label: this.translocoService.translate("common.targetings"),
          size: "md",
        },
        {
          stepId: "asins",
          label: this.translocoService.translate("common.asins"),
          size: "md",
        },
      ],
    });
  }

  goToStep(stepId: IFormStep<SdStrategyCreationFormModel>) {
    this.formNavigationService.goToStep(stepId);
  }

  cancel() {
    if (this.modalData) {
      this.modalRef?.close();
    } else {
      void this.router.navigate(["/advertising/sponsored-display"], { queryParamsHandling: "merge" });
    }
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    this.formNavigationService.next();
  }

  submitForm() {
    if (this.creationForm().invalid()) {
      return;
    }

    this.isCreatingStrategy.set(true);

    const formValue = this.creationForm().value();
    const targeting = formValue.targetings;
    const targetingType = targeting!.targetingType;
    const algoModeConfig = formValue.setup.algoModeConfig!;

    const limitReached = this.sdStrategyService.isStrategyLimitReached(
      this.accountMarketplace().accountId,
      this.strategies() ?? [],
      CampaignType.SD,
    );

    const tactics: StrategyTactic[] =
      targetingType === SdTargetingType.PRODUCT
        ? [
            {
              tacticType: TacticType.LEGACY,
              segmentId: targeting.segment!.segmentId,
            },
          ]
        : [];

    const audienceTargetings: AudienceTargeting[] =
      targetingType === SdTargetingType.REMARKETING
        ? [
            {
              expressionType: targeting.audienceExpressionType!,
              matchType: targeting.audienceMatchType!,
              lookback: targeting.audienceLookback!,
            },
          ]
        : [];

    const strategy: SdStrategyCreateRequest = {
      state: limitReached ? StrategyStateEnum.PAUSED : StrategyStateEnum.ENABLED,
      name: formValue.setup.strategyName,
      asins: formValue.asins.asins,
      ...algoModeConfig,
      acosTarget: algoModeConfig.acosTarget ? algoModeConfig.acosTarget / 100 : undefined,
      disableOtherQueries: targetingType !== SdTargetingType.PRODUCT_AI_TARGETING,
      tacticSegmentId: tactics.length ? tactics[0].segmentId : undefined,
      audienceTargetings,
    };

    this.sdStrategyService
      .createStrategy(strategy)
      .pipe(
        take(1),
        finalize(() => this.isCreatingStrategy.set(false)),
      )
      .subscribe({
        next: (created: Strategy) => {
          this.toastrService.success(
            this.translocoService.translate("sd-strategy-creation.strategy_successfully_created"),
            this.translocoService.translate("sd-strategy-creation.strategy_created"),
          );
          this.strategyCreated.emit(created);
          this.modalRef?.close(true);
        },
        error: (e: string) => {
          this.toastrService.error(
            this.translocoService.translate("sd-strategy-creation.error_when_creating_strategy") + ": " + e,
            this.translocoService.translate("sd-strategy-creation.strategy_creation_error"),
          );
        },
      });
  }
}
